// src/components/DevTools/DevicePreview.tsx

import React from 'react'
import {
  Play,
  RotateCcw,
  Maximize2,
  X,
  Wifi,
  Battery,
  Signal,
  Lock,
  ArrowLeft,
  ArrowRight,
  RotateCw,
} from 'lucide-react'
import { Device, EnhancedLighthouseMetrics } from '../../types'
import { getScaleFactor, getPerformanceClass, getCleanUrl } from '../../utils/helpers'

interface DevicePreviewProps {
  device: Device
  url: string
  deviceRotations: Map<string, boolean>
  performanceData: Map<string, EnhancedLighthouseMetrics>
  runningTests: Set<string>
  realDeviceBehavior: boolean
  accessibilityMode: 'none' | 'contrast' | 'colorblind'
  showGrid: boolean
  onToggleRotation: (deviceName: string) => void
  onRemove: () => void
  onFullscreen: () => void
  onRunTest: (deviceName: string) => void
}

export function DevicePreview({
  device,
  url,
  deviceRotations,
  performanceData,
  runningTests,
  realDeviceBehavior,
  accessibilityMode,
  showGrid,
  onToggleRotation,
  onRemove,
  onFullscreen,
  onRunTest,
}: DevicePreviewProps) {
  const isRotated = deviceRotations.get(device.name) || false
  const scaleFactor = getScaleFactor(device, deviceRotations)
  const displayWidth = isRotated ? device.height : device.width
  const displayHeight = isRotated ? device.width : device.height
  const metrics = performanceData.get(device.name)
  const isRunningTest = runningTests.has(device.name)

  // Calculate chrome heights
  let chromeHeight = 0
  if (realDeviceBehavior) {
    if (device.hasNotch) chromeHeight += 32
    if (device.statusBar) {
      chromeHeight += device.os === 'android' ? 24 : 44
    }
    if (device.browserUI) {
      chromeHeight += device.category === 'phone' ? 0 : 72
    }
  }

  const viewportHeight = displayHeight - chromeHeight

  return (
    <div className="device-preview">
      <div className="device-header">
        <div>
          <div className="device-title">
            {device.icon}
            {device.name}
          </div>
          <div className="device-info">
            <span>
              {displayWidth} × {displayHeight}
            </span>
            <span>{Math.round(scaleFactor * 100)}%</span>
            {metrics && (
              <span
                style={{ color: getPerformanceClass(metrics.performance).color, fontWeight: 500 }}
              >
                {metrics.performance}
              </span>
            )}
          </div>
        </div>

        <div className="device-actions">
          <button
            onClick={() => onRunTest(device.name)}
            className="test-btn"
            disabled={isRunningTest}
            title="Run Comprehensive Test"
          >
            <Play className="w-2 h-2" />
          </button>
          <button
            onClick={() => onToggleRotation(device.name)}
            className={`action-btn ${isRotated ? 'active' : ''}`}
            title="Rotate"
          >
            <RotateCcw className="w-3 h-3" />
          </button>
          <button onClick={onFullscreen} className="action-btn" title="Fullscreen">
            <Maximize2 className="w-3 h-3" />
          </button>
          <button onClick={onRemove} className="action-btn remove-btn" title="Remove device">
            <X className="w-3 h-3" />
          </button>
        </div>
      </div>

      <div className="device-body">
        <div
          className="device-chrome"
          style={{
            width: displayWidth * scaleFactor,
            height: displayHeight * scaleFactor,
          }}
        >
          {/* Enhanced Real Device UI */}
          {realDeviceBehavior && (
            <>
              {/* iOS Notch */}
              {device.hasNotch && (
                <div
                  className="ios-notch"
                  style={{ transform: `translateX(-50%) scale(${scaleFactor})` }}
                />
              )}

              {/* Status Bars */}
              {device.statusBar && (
                <div
                  className={device.os === 'android' ? 'android-status-bar' : 'ios-status-bar'}
                  style={{
                    height: (device.os === 'android' ? 24 : 44) * scaleFactor,
                    fontSize: (device.os === 'android' ? 12 : 15) * scaleFactor,
                    padding: `0 ${(device.os === 'android' ? 16 : 20) * scaleFactor}px`,
                  }}
                >
                  {device.os === 'android' ? (
                    <>
                      <div className="android-status-left">
                        <span>12:34</span>
                      </div>
                      <div className="android-status-right">
                        <Signal style={{ width: 10 * scaleFactor, height: 10 * scaleFactor }} />
                        <Wifi style={{ width: 10 * scaleFactor, height: 10 * scaleFactor }} />
                        <Battery style={{ width: 10 * scaleFactor, height: 10 * scaleFactor }} />
                        <span>100%</span>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="ios-status-left">
                        <Signal style={{ width: 12 * scaleFactor, height: 12 * scaleFactor }} />
                        <Wifi style={{ width: 12 * scaleFactor, height: 12 * scaleFactor }} />
                      </div>
                      <span>9:41</span>
                      <div className="ios-status-right">
                        <span>100%</span>
                        <Battery style={{ width: 14 * scaleFactor, height: 14 * scaleFactor }} />
                      </div>
                    </>
                  )}
                </div>
              )}

              {/* Desktop/Laptop Browser Chrome */}
              {device.browserUI &&
                (device.category === 'laptop' ||
                  device.category === 'desktop' ||
                  device.category === 'special') && (
                  <div
                    className={device.os === 'macos' ? 'macos-chrome' : 'windows-chrome'}
                    style={{ height: 72 * scaleFactor }}
                  >
                    <div
                      className="titlebar"
                      style={{ height: 32 * scaleFactor, padding: `0 ${12 * scaleFactor}px` }}
                    >
                      <div className="browser-controls">
                        <div
                          className="browser-btn btn-close"
                          style={{
                            width: 12 * scaleFactor,
                            height: 12 * scaleFactor,
                          }}
                        />
                        <div
                          className="browser-btn btn-minimize"
                          style={{
                            width: 12 * scaleFactor,
                            height: 12 * scaleFactor,
                          }}
                        />
                        <div
                          className="browser-btn btn-maximize"
                          style={{
                            width: 12 * scaleFactor,
                            height: 12 * scaleFactor,
                          }}
                        />
                      </div>
                      <div className="window-title" style={{ fontSize: 13 * scaleFactor }}>
                        {url.replace('http://', '').replace('https://', '').split('/')[0]}
                      </div>
                    </div>
                    <div
                      className="toolbar"
                      style={{ padding: `${6 * scaleFactor}px ${12 * scaleFactor}px` }}
                    >
                      <div className="nav-buttons">
                        <button
                          className="nav-btn"
                          style={{
                            width: 24 * scaleFactor,
                            height: 24 * scaleFactor,
                          }}
                          disabled
                        >
                          <ArrowLeft
                            style={{ width: 12 * scaleFactor, height: 12 * scaleFactor }}
                          />
                        </button>
                        <button
                          className="nav-btn"
                          style={{
                            width: 24 * scaleFactor,
                            height: 24 * scaleFactor,
                          }}
                          disabled
                        >
                          <ArrowRight
                            style={{ width: 12 * scaleFactor, height: 12 * scaleFactor }}
                          />
                        </button>
                        <button
                          className="nav-btn"
                          style={{
                            width: 24 * scaleFactor,
                            height: 24 * scaleFactor,
                          }}
                        >
                          <RotateCw style={{ width: 12 * scaleFactor, height: 12 * scaleFactor }} />
                        </button>
                      </div>
                      <div
                        className="address-bar"
                        style={{
                          height: 28 * scaleFactor,
                          fontSize: 13 * scaleFactor,
                          padding: `0 ${8 * scaleFactor}px`,
                        }}
                      >
                        <Lock
                          style={{
                            width: 12 * scaleFactor,
                            height: 12 * scaleFactor,
                            opacity: 0.7,
                          }}
                        />
                        <span>
                          {url.replace('http://', '').replace('https://', '').split('/')[0]}
                        </span>
                      </div>
                    </div>
                  </div>
                )}
            </>
          )}

          {/* Viewport */}
          <div
            className="device-viewport"
            style={{
              width: displayWidth * scaleFactor,
              height: (realDeviceBehavior ? viewportHeight : displayHeight) * scaleFactor,
            }}
          >
            {isRunningTest && (
              <div className="device-loading">
                <div className="spinner" />
                <span>Running comprehensive analysis...</span>
              </div>
            )}

            <iframe
              src={getCleanUrl(url)}
              title={`${device.name} device preview`}
              width={displayWidth}
              height={realDeviceBehavior ? viewportHeight : displayHeight}
              className="device-iframe"
              style={{
                transform: `scale(${scaleFactor})`,
              }}
            />

            {/* Accessibility Filters */}
            {accessibilityMode !== 'none' && (
              <div
                className={`accessibility-filter ${accessibilityMode === 'contrast' ? 'contrast-filter' : 'colorblind-filter'}`}
              />
            )}

            {showGrid && <div className="grid-overlay" />}
          </div>
        </div>
      </div>
    </div>
  )
}
